import { useState, useEffect } from 'react';
import { api } from '../lib/api';

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [creditMemos, setCreditMemos] = useState([]);
  const [matched, setMatched] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.getInvoices()
      .then(res => {
        setMatched(res.matched !== false);
        setInvoices(res.invoices || []);
        setCreditMemos(res.creditMemos || []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{color:'rgba(0,0,0,0.4)',padding:'3rem',textAlign:'center'}}>Loading invoices...</div>;

  // Invoices and credit memos are both transactions on the account; show them in
  // one chronological list (newest first), tagged by kind so each renders distinctly.
  const transactions = [
    ...invoices.map(inv => ({ ...inv, kind: 'invoice' })),
    ...creditMemos.map(cm => ({ ...cm, kind: 'credit' })),
  ].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  const subtitleParts = [`${invoices.length} invoice${invoices.length !== 1 ? 's' : ''}`];
  if (creditMemos.length > 0) subtitleParts.push(`${creditMemos.length} credit${creditMemos.length !== 1 ? 's' : ''}`);
  subtitleParts.push('From QuickBooks');

  return (
    <div>
      <div data-page-header style={styles.header}>
        <h1 style={styles.title}>Invoices</h1>
        <p style={styles.subtitle}>{subtitleParts.join(' · ')}</p>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {!matched && !error && (
        <div style={styles.empty}>
          We couldn't match your account to a QuickBooks customer record yet. Please contact us at <a href="mailto:orders@silverservice.com" style={{color:'#A50B18'}}>orders@silverservice.com</a> and we'll get it linked.
        </div>
      )}

      {matched && transactions.length === 0 && !error && (
        <div style={styles.empty}>No invoices found for your account.</div>
      )}

      <div style={styles.list}>
        {transactions.map(tx => (
          tx.kind === 'credit'
            ? <CreditCard key={`cm-${tx.id}`} memo={tx} onOpen={() => setSelected(tx)} />
            : <InvoiceCard key={`inv-${tx.id}`} invoice={tx} onOpen={() => setSelected(tx)} />
        ))}
      </div>

      {selected && <TransactionModal tx={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

// QuickBooks dates are calendar days ("2026-10-15"). new Date() reads those as
// midnight UTC, which is the evening before in Colorado.
function fmtDate(d, opts) {
  if (!d) return '—';
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  const date = day ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3])) : new Date(d);
  return date.toLocaleDateString('en-US', opts);
}

function InvoiceCard({ invoice: inv, onOpen }) {
  return (
    <div style={styles.card} onClick={onOpen}>
      <div style={styles.cardLeft}>
        <div style={styles.invNum}>Invoice #{inv.number || inv.id}</div>
        <div style={styles.invDate}>
          {fmtDate(inv.date, {month:'short', day:'numeric', year:'numeric'})}
          {inv.dueDate && <span style={styles.dueText}> · Due {fmtDate(inv.dueDate, {month:'short',day:'numeric'})}</span>}
        </div>
      </div>
      <div style={styles.cardRight}>
        <span style={{...styles.statusBadge, ...(inv.status === 'Paid' ? styles.paid : styles.open)}}>
          {inv.status}
        </span>
        <span style={styles.total}>${(inv.total || 0).toFixed(2)}</span>
        {inv.status !== 'Paid' && inv.balance > 0 && (
          <span style={styles.balance}>${inv.balance.toFixed(2)} due</span>
        )}
        <span style={styles.viewLink}>View</span>
      </div>
    </div>
  );
}

function CreditCard({ memo: cm, onOpen }) {
  const available = cm.status === 'Available';
  return (
    <div style={styles.card} onClick={onOpen}>
      <div style={styles.cardLeft}>
        <div style={styles.invNum}>Credit Memo #{cm.number || cm.id}</div>
        <div style={styles.invDate}>{fmtDate(cm.date, {month:'short', day:'numeric', year:'numeric'})}</div>
      </div>
      <div style={styles.cardRight}>
        <span style={{...styles.statusBadge, ...(available ? styles.creditAvail : styles.creditApplied)}}>
          {available ? 'Credit available' : 'Applied'}
        </span>
        <span style={styles.creditAmt}>${(cm.total || 0).toFixed(2)}</span>
        {available && cm.remainingCredit > 0 && cm.remainingCredit !== cm.total && (
          <span style={styles.creditRemaining}>${cm.remainingCredit.toFixed(2)} left</span>
        )}
        <span style={styles.viewLink}>View</span>
      </div>
    </div>
  );
}

function TransactionModal({ tx, onClose }) {
  const isCredit = tx.kind === 'credit';
  const [detail, setDetail] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const label = isCredit ? 'Credit Memo' : 'Invoice';
  const title = `${label} #${tx.number || tx.id}`;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const detailReq = isCredit ? api.getCreditMemo(tx.id) : api.getInvoice(tx.id);
    const pdfReq = isCredit ? api.getCreditMemoPdf(tx.id) : api.getInvoicePdf(tx.id);
    Promise.all([detailReq, pdfReq])
      .then(([d, p]) => {
        if (!alive) return;
        setDetail(d);
        setPdf(p);
      })
      .catch(e => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tx.id, isCredit]);

  const pdfDataUri = pdf?.base64 ? `data:application/pdf;base64,${pdf.base64}` : null;

  function download() {
    if (!pdfDataUri) return;
    const a = document.createElement('a');
    a.href = pdfDataUri;
    a.download = `${isCredit ? 'creditmemo' : 'invoice'}-${tx.number || tx.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  const badgeStyle = isCredit
    ? (tx.status === 'Available' ? styles.creditAvail : styles.creditApplied)
    : (tx.status === 'Paid' ? styles.paid : styles.open);
  const badgeText = isCredit ? (tx.status === 'Available' ? 'Credit available' : 'Applied') : tx.status;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div>
            <div style={styles.modalTitle}>{title}</div>
            <div style={styles.modalSub}>
              <span style={{...styles.statusBadge, ...badgeStyle}}>{badgeText}</span>
              <span style={{marginLeft:'0.6rem', fontFamily:'monospace', color:isCredit ? '#1e6f3a' : '#A50B18', fontWeight:600}}>${(tx.total || 0).toFixed(2)}</span>
            </div>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={styles.modalActions}>
          <button style={styles.downloadBtn} onClick={download} disabled={!pdfDataUri}>⬇ Download PDF</button>
        </div>

        {!isCredit && !loading && !error && detail?.payLink && tx.status !== 'Paid' && (
          <a style={styles.payBanner} href={detail.payLink} target="_blank" rel="noopener noreferrer">
            <span style={styles.payBannerText}>
              Pay Invoice
              {tx.balance > 0 && <span style={styles.payBannerAmt}> · ${tx.balance.toFixed(2)} due</span>}
            </span>
            <span style={styles.payBannerCta}>Pay in QuickBooks →</span>
          </a>
        )}

        <div style={styles.pdfFrame}>
          {loading && <div style={styles.pdfMsg}>Loading {label.toLowerCase()}…</div>}
          {error && <div style={styles.pdfMsg}>Could not load the {label.toLowerCase()}: {error}</div>}
          {!loading && !error && pdfDataUri && (
            <iframe title={title} src={pdfDataUri} style={styles.iframe} />
          )}
          {!loading && !error && !pdfDataUri && <div style={styles.pdfMsg}>No PDF available for this {label.toLowerCase()}.</div>}
        </div>
      </div>
    </div>
  );
}

const styles = {
  header: { marginBottom:'1.5rem' },
  title: { fontSize:'1.5rem', fontWeight:700, color:'#333', margin:0 },
  subtitle: { fontSize:'0.82rem', color:'rgba(0,0,0,0.4)', margin:'0.3rem 0 0' },
  errorBanner: { background:'rgba(231,76,60,0.08)', border:'1px solid rgba(231,76,60,0.25)', borderRadius:8, padding:'1rem 1.25rem', color:'#c0392b', marginBottom:'1rem' },
  empty: { textAlign:'center', color:'rgba(0,0,0,0.45)', padding:'4rem 1rem', lineHeight:1.6 },
  list: { display:'flex', flexDirection:'column', gap:'0.75rem' },
  card: { background:'#fff', border:'1px solid #e0e0e0', borderRadius:10, padding:'1rem 1.25rem', boxShadow:'0 1px 3px rgba(0,0,0,0.04)', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'0.75rem' },
  cardLeft: { display:'flex', flexDirection:'column', gap:2 },
  invNum: { fontSize:'0.95rem', fontWeight:600, color:'#333' },
  invDate: { fontSize:'0.8rem', color:'rgba(0,0,0,0.4)' },
  dueText: { color:'rgba(0,0,0,0.4)' },
  cardRight: { display:'flex', alignItems:'center', gap:'1rem', flexWrap:'wrap' },
  statusBadge: { fontSize:'0.72rem', fontWeight:600, borderRadius:12, padding:'0.22rem 0.7rem', letterSpacing:'0.02em' },
  paid: { color:'#1e6f3a', background:'#e6f4ea' },
  open: { color:'#8a5a00', background:'#fff4d1' },
  creditAvail: { color:'#1e5f8a', background:'#e1f0fb' },
  creditApplied: { color:'rgba(0,0,0,0.5)', background:'#eee' },
  total: { fontFamily:'monospace', color:'#A50B18', fontWeight:600 },
  balance: { fontFamily:'monospace', fontSize:'0.85rem', fontWeight:600, color:'#c0392b' },
  creditAmt: { fontFamily:'monospace', color:'#1e6f3a', fontWeight:600 },
  creditRemaining: { fontFamily:'monospace', fontSize:'0.85rem', fontWeight:600, color:'#1e6f3a' },
  viewLink: { color:'#A50B18', fontSize:'0.85rem', fontWeight:600 },

  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:'1.5rem' },
  modal: { background:'#fff', borderRadius:12, width:'100%', maxWidth:820, maxHeight:'90vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 10px 40px rgba(0,0,0,0.25)' },
  modalHeader: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'1.25rem 1.5rem', borderBottom:'1px solid #e0e0e0' },
  modalTitle: { fontSize:'1.15rem', fontWeight:700, color:'#333' },
  modalSub: { marginTop:'0.4rem', display:'flex', alignItems:'center' },
  closeBtn: { background:'none', border:'none', fontSize:'1.1rem', color:'rgba(0,0,0,0.4)', cursor:'pointer' },
  modalActions: { display:'flex', gap:'0.75rem', padding:'0.9rem 1.5rem', borderBottom:'1px solid #f0f0f0', flexWrap:'wrap' },
  downloadBtn: { background:'#fff', color:'#333', border:'1px solid #cfcfcf', borderRadius:6, padding:'0.55rem 1.1rem', fontWeight:600, cursor:'pointer', fontSize:'0.85rem' },
  payBanner: { display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem', flexWrap:'wrap', padding:'0.8rem 1.5rem', background:'#eafaf0', borderBottom:'1px solid #c8ecd5', textDecoration:'none', color:'#1e6f3a', cursor:'pointer' },
  payBannerText: { fontSize:'0.9rem', fontWeight:600 },
  payBannerAmt: { fontFamily:'monospace', fontWeight:600 },
  payBannerCta: { background:'#27ae60', color:'#fff', borderRadius:6, padding:'0.5rem 1.1rem', fontWeight:600, fontSize:'0.85rem', whiteSpace:'nowrap' },
  pdfFrame: { flex:1, minHeight:360, background:'#f5f5f5', display:'flex', alignItems:'center', justifyContent:'center' },
  pdfMsg: { color:'rgba(0,0,0,0.45)', padding:'2rem', textAlign:'center' },
  iframe: { width:'100%', height:'100%', minHeight:'60vh', border:'none' },
};
